
import React, { useEffect, useState } from "react";
import MainLayout from "../layouts/MainLayout";
import Swal from 'sweetalert2';
import '../assets/styles/loader.css';
import '../assets/styles/pagination.css';
import { Select } from "antd";
import { useNavigate } from "react-router-dom";
import { fetchModuleScreens } from "../services/roleMenuServices";
import { fetchWithAuth } from "../utils/api";

export default function ModuleScreens() {

    const { Option } = Select;
    const [sessionUserData, setSessionUserData] = useState([]);
    const [moduleScreens, setModuleScreens] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [selectedModule, setSelectedModule] = useState(null);
    const [modules, setModules] = useState([]);
    const [actionName, setActionName] = useState('');
    const [addEnumLoading, setAddEnumLoading] = useState(false);
    const [actionEnums, setActionEnums] = useState([]);
    const [editRowId, setEditRowId] = useState(null);
    const [rowSelections, setRowSelections] = useState({});
    const [enumSearch, setEnumSearch] = useState("");
    const navigate = useNavigate()

    useEffect(() => {
        const userDataString = sessionStorage.getItem("userData");
        if (userDataString) {
            const userData = JSON.parse(userDataString);
            setSessionUserData(userData);
        } else {
            navigate("/");
        }
    }, [navigate]);

    const fetchActionEnums = async () => {
        try {
            const response = await fetchWithAuth(
                `ADMINRoutes/CWIGetDDLItems?OrgId=0&UserId=0`, {
                method: 'GET',
            }
            );
            if (!response.ok) throw new Error("Network response was not ok");

            const data = await response.json();

            const filteredEnums = data.ResultData.filter(
                (item) => item.DDLName === "ActionEnums"
            );

            const enumsFormatted = filteredEnums.map((item) => ({
                label: item.DisplayValue,
                value: item.ItemId,
            }));

            setActionEnums(enumsFormatted);
        } catch (error) {
            console.error("Failed to fetch ActionEnums:", error);
        }
    }

    useEffect(() => {
        fetchActionEnums();
    }, []);

    const fetchModulesData = async () => {
        try {
            const response = await fetchWithAuth(`AdminRoutes/getModules`, {
                method: 'GET',
            });
            if (response.ok) {
                const data = await response.json();
                const allModules = data.ResultData || [];

                setModules(allModules);
            } else {
                console.error("Failed to fetch menu data:", response.statusText);
            }
        } catch (error) {
            console.error("Error fetching menu data:", error.message);
        }
    };

    useEffect(() => {
        fetchModulesData();
    }, []);

    useEffect(() => {
        if (selectedModule) {
            getModuleScreens();
        }
    }, [selectedModule]);

    const getModuleScreens = async () => {
        setDataLoading(true);
        try {
            const data = await fetchModuleScreens(selectedModule);
            setModuleScreens(data);
        } catch (error) {
            console.error('Failed to load departments:', error);
        } finally {
            setDataLoading(false);
        }
    };

    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 10;
    const totalPages = moduleScreens?.length ? Math.ceil(moduleScreens?.length / recordsPerPage) : 0;

    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = moduleScreens?.slice(indexOfFirstRecord, indexOfLastRecord);

    const getPaginationNumbers = () => {
        const visiblePages = [];
        if (totalPages <= 6) {
            for (let i = 1; i <= totalPages; i++) visiblePages.push(i);
        } else {
            if (currentPage <= 3) {
                visiblePages.push(1, 2, 3, "...", totalPages - 2, totalPages - 1, totalPages);
            }
            else if (currentPage > 3 && currentPage < totalPages - 2) {
                visiblePages.push(1, 2, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages - 1, totalPages);
            }
            else {
                visiblePages.push(1, 2, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            }
        }
        return visiblePages;
    };

    const handlePageClick = (page) => {
        if (page !== "...") setCurrentPage(page);
    };

    const handlePrevious = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleNext = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handleClear = () => {
        setActionName('');
    };

    const maxEnumId = Math.max(...actionEnums.map((op) => op.value));
    const nextEnumId = maxEnumId + 1;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!actionName.trim()) {
            Swal.fire('Validation Error', 'Action name cannot be empty.', 'warning');
            return;
        }

        const payload = {
            EnumId: nextEnumId,
            ActionName: actionName.toUpperCase(),
            CreatedBy: sessionUserData?.Id,
        };

        try {
            setAddEnumLoading(true);
            const response = await fetchWithAuth(`ADMINRoutes/AddEnumActions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (response.ok && result?.ResultData?.Status === 'Success') {
                Swal.fire('Success', 'Enum added successfully!', 'success');
                setActionName('');
                fetchActionEnums();
            } else {
                Swal.fire('Error', result?.ResultData?.Message || 'Failed to add enum.', 'error');
            }
        } catch (error) {
            console.error('Fetch error:', error);
            Swal.fire('Error', 'Something went wrong. Please try again later.', 'error');
        } finally {
            setAddEnumLoading(false);
        }
    };

    const handleEdit = (menuId, existingIds) => {
        setEditRowId(menuId);
        setRowSelections((prev) => ({
            ...prev,
            [menuId]: existingIds, // initialize with current ActionIds
        }));
    };

    const handleSave = async (menuId) => {
        const selectedIds = rowSelections[menuId] || [];
        const actionIdString = selectedIds.join(',');

        const payload = {
            ActionIds: actionIdString,
            UpdatedBy: sessionUserData?.Id,
            MenuId: menuId,
        };

        try {
            const response = await fetchWithAuth(`ADMINRoutes/UpdateMenuEnums`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (result?.ResultData?.Status === 'Success') {
                Swal.fire({
                    icon: 'success',
                    title: 'Saved',
                    text: 'Action IDs updated successfully!',
                    timer: 1500,
                    showConfirmButton: false,
                });
                getModuleScreens();
            } else {
                throw new Error(result?.ResultData?.Message || 'Something went wrong');
            }
        } catch (error) {
            console.error('Error saving ActionIds:', error);
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: error.message || 'Failed to save ActionIds.',
            });
        } finally {
            setEditRowId(null);
        }
    };

    const handleChange = (menuId, selectedValues) => {
        setRowSelections((prev) => ({
            ...prev,
            [menuId]: selectedValues,
        }));
    };

    const filteredActionEnums = actionEnums?.filter((item) => {
        const search = enumSearch.trim().toLowerCase();
        if (!search) return true;

        const labelMatch = item?.label?.toLowerCase().includes(search);
        const valueMatch = String(item?.value || "").includes(search);

        return labelMatch || valueMatch;
    }) || [];


    return (
        <MainLayout>
            <div className="content">
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4 className="fw-bold">Module-Screens</h4>
                            <h6>Manage your module-screens</h6>
                        </div>
                    </div>
                    <ul className="table-top-head">
                        <li>
                            <button
                                className="btn btn-primary"
                                data-bs-toggle="offcanvas"
                                data-bs-target="#offcanvasRightAdd"
                                aria-controls="offcanvasRightAdd"
                            >Add Enums</button>
                        </li>
                        <li>
                            <a data-bs-toggle="tooltip" data-bs-placement="top" title="Refresh"><i className="ti ti-refresh"></i></a>
                        </li>
                    </ul>
                </div>
                <div className="row justify-content-evenly">
                    <div className="card col-12">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <div className="row w-100 align-items-center">
                                    {/* Search Input */}
                                    <div className="col-md-3 mb-2">
                                        <Select
                                            showSearch
                                            placeholder="Choose Module"
                                            className="w-100"
                                            value={selectedModule}
                                            onChange={(value) => setSelectedModule(value)}
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                option.children.toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {modules?.map((mod) => (
                                                <Option key={mod.Id} value={mod.Id}>
                                                    {mod.ModuleName}
                                                </Option>
                                            ))}
                                        </Select>
                                    </div>
                                </div>
                            </div>
                            <div className="card-toolbar">
                                <div className="d-flex justify-content-end" data-kt-customer-table-toolbar="base"></div>
                                <div className="d-flex justify-content-end align-items-center d-none" data-kt-customer-table-toolbar="selected">
                                    <div className="fw-bold me-5">
                                        <span className="me-2" data-kt-customer-table-select="selected_count"></span>Selected</div>
                                    <button type="button" className="btn btn-danger" data-kt-customer-table-select="delete_selected">Delete Selected</button>
                                </div>
                            </div>
                        </div>
                        <div className="card-body pt-0">
                            <div className="table-responsive">
                                <table className="table align-middle table-row-dashed fs-6 gy-5" id="kt_customers_table">
                                    <thead>
                                        <tr className="text-start text-gray-500 fw-bold fs-7 text-uppercase gs-0">
                                            <th className="">S.No</th>
                                            <th className="min-w-125px">Screen Name</th>
                                            <th className="min-w-125px">Function Enum's</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="fw-semibold text-gray-600">
                                        {dataLoading ? (
                                            <tr>
                                                <td colSpan="12" className="text-center">
                                                    <div className="container"></div>
                                                </td>
                                            </tr>
                                        ) : currentRecords && currentRecords.length > 0 ? (
                                            currentRecords.map((item, index) => {
                                                const itemId = item.Id || index;
                                                const isEditing = editRowId === itemId;
                                                const currentSelected =
                                                    rowSelections[itemId] ?? item.ActionIds?.split(',').map(Number) ?? [];

                                                return (
                                                    <tr key={itemId}>
                                                        <td>{(currentPage - 1) * recordsPerPage + index + 1}</td>
                                                        <td>
                                                            <a className="text-gray-800 text-hover-primary mb-1">{item.MenuName}</a>
                                                        </td>
                                                        <td>
                                                            <Select
                                                                mode="multiple"
                                                                showSearch
                                                                placeholder="Select Operation"
                                                                className="w-100"
                                                                disabled={!isEditing}
                                                                value={currentSelected}
                                                                onChange={(values) => handleChange(itemId, values)}
                                                                optionFilterProp="children"
                                                                filterOption={(input, option) =>
                                                                    option?.children?.toLowerCase().includes(input.toLowerCase())
                                                                }
                                                            >
                                                                {actionEnums?.map((op) => (
                                                                    <Option key={op.value} value={op.value}>
                                                                        {op.label}
                                                                    </Option>
                                                                ))}
                                                            </Select>
                                                        </td>
                                                        <td className="text-center">
                                                            {isEditing ? (
                                                                <i
                                                                    className="fa-solid fa-floppy-disk text-success me-2"
                                                                    title="Save"
                                                                    onClick={() => handleSave(itemId)}
                                                                    style={{ cursor: 'pointer' }}
                                                                ></i>
                                                            ) : (
                                                                <i
                                                                    className="fa-solid fa-pen text-primary me-2"
                                                                    title="Edit"
                                                                    onClick={() =>
                                                                        handleEdit(itemId, item.ActionIds?.split(',').map(Number) || [])
                                                                    }
                                                                    style={{ cursor: 'pointer' }}
                                                                    onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.2)')}
                                                                    onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                                                                ></i>
                                                            )}

                                                            {/* <i
                                                                className="fa-regular fa-trash-can text-danger ms-2"
                                                                title="Delete"
                                                                onClick={() => handleDelete(item)}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    transition: 'transform 0.2s',
                                                                }}
                                                                onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.2)')}
                                                                onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                                                            ></i> */}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="12" className="text-center">
                                                    <p>No Data Available</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>

                                </table>
                                <div className="dt-paging paging_simple_numbers">
                                    <nav aria-label="pagination">
                                        <ul className="pagination">
                                            <li
                                                className={`dt-paging-button page-item ${currentPage === 1 ? "disabled" : ""}`}
                                            >
                                                <button
                                                    className="page-link previous"
                                                    role="link"
                                                    type="button"
                                                    aria-controls="kt_customers_table"
                                                    aria-disabled={currentPage === 1}
                                                    aria-label="Previous"
                                                    onClick={handlePrevious}
                                                >
                                                    <i className="previous"></i>
                                                </button>
                                            </li>

                                            {/* Page Numbers */}
                                            {getPaginationNumbers().map((page, index) => (
                                                <li
                                                    key={index}
                                                    className={`dt-paging-button page-item ${page === currentPage ? "active" : ""}`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        role="link"
                                                        type="button"
                                                        aria-controls="kt_customers_table"
                                                        aria-current={page === currentPage ? "page" : undefined}
                                                        onClick={() => handlePageClick(page)}
                                                        disabled={page === "..."}
                                                    >
                                                        {page}
                                                    </button>
                                                </li>
                                            ))}

                                            {/* Next Button */}
                                            <li
                                                className={`dt-paging-button page-item ${currentPage === totalPages ? "disabled" : ""}`}
                                            >
                                                <button
                                                    className="page-link next"
                                                    role="link"
                                                    type="button"
                                                    aria-controls="kt_customers_table"
                                                    aria-disabled={currentPage === totalPages}
                                                    aria-label="Next"
                                                    onClick={handleNext}
                                                >
                                                    <i className="next"></i>
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <>
                    <div
                        className="offcanvas offcanvas-end"
                        tabIndex="-1"
                        id="offcanvasRightAdd"
                        aria-labelledby="offcanvasRightLabel"
                        style={{ minWidth: "35%", maxWidth: "35%" }}
                    >
                        <form autoComplete="off" onSubmit={handleSubmit}>
                            <div className="offcanvas-header border-bottom">
                                <h5 id="offcanvasRightLabel" className="mb-0">Add Enum</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="offcanvas"
                                    aria-label="Close"
                                    onClick={handleClear}
                                ></button>
                            </div>

                            <div className="offcanvas-body" style={{
                                flex: 1,
                                overflowY: 'auto',
                                paddingBottom: '2rem',
                                maxHeight: 'calc(100vh - 100px)'
                            }}>
                                <div className="row align-items-end g-2">
                                    <div className="col-4">
                                        <label className="form-label">Enum ID</label>
                                        <input
                                            type="text"
                                            className="form-control text-info fw-bold"
                                            value={nextEnumId}
                                            style={{ cursor: 'not-allowed' }}
                                            disabled
                                        />
                                    </div>

                                    <div className="col-5">
                                        <label className="form-label">Action Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter name"
                                            value={actionName}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\s/g, '');
                                                setActionName(value.toUpperCase());
                                            }}
                                        />
                                    </div>

                                    <div className="col-3 d-grid">
                                        <label className="form-label invisible">Submit</label>
                                        <button
                                            className="btn btn-primary"
                                            type="submit"
                                            disabled={addEnumLoading}
                                        >
                                            {addEnumLoading ? '...' : 'Add'}
                                        </button>
                                    </div>
                                </div>

                                <hr className="my-4" />
                                <div className="mb-3">
                                    <label className="form-label">Search Enum</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by label or value"
                                        value={enumSearch}
                                        onChange={(e) => setEnumSearch(e.target.value)}
                                    />
                                </div>


                                <h6 className="mb-2">Existing Enums</h6>
                                <ul className="list-group small">
                                    {filteredActionEnums.map((item) => (
                                        <li
                                            className="list-group-item py-2 px-3 d-flex justify-content-between align-items-center"
                                            key={item.value}
                                        >
                                            <span className="text-truncate fw-semibold">{item.label}</span>
                                            <span className="badge bg-secondary">{item.value}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </form>
                    </div>

                </>
            </div>
        </MainLayout>
    )
}